import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { LifeBuoy } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams, FetchResult } from '@/components/DataTable/types'
import { fetchSupportTickets, type AdminSupportTicket } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { TicketThread, isAppealTicket } from './TicketThread'

/**
 * A7 — тікети підтримки (без апеляцій на бан/заморозку — ті живуть окремо на /appeals, щоб не
 * плутати "людина написала з питанням" і "людину/канал обмежили, вона оскаржує"). GET
 * /admin/support: реальні дані з SupportTicketAggregate, наповнюється через діалог з ботом "Grom
 * Support" (569999) — раніше це був звичайний акаунт без жодної логіки, повідомлення нікуди не
 * потрапляли.
 */
export function SupportScreen() {
  const t = useT()
  const [reloadKey, setReloadKey] = useState(0)

  const fetcher = useCallback(async (params: FetchParams): Promise<FetchResult<AdminSupportTicket>> => {
    const result = await fetchSupportTickets(params)
    return { ...result, rows: result.rows.filter((r) => !isAppealTicket(r)) }
  }, [])

  const columns = useMemo<AdminColumn<AdminSupportTicket>[]>(
    () => [
      {
        key: 'user_id',
        header: t.support.user,
        type: 'text',
        width: 160,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'status',
        header: t.support.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.support.statusOpen, status: 'review' },
          { value: 'closed', label: t.support.statusClosed, status: 'deleted' },
        ],
      },
      { key: 'created_at', header: t.support.createdAt, type: 'date', width: 140 },
    ],
    [t],
  )

  return (
    <>
      <PageHeader title={t.support.title} screenId="A7.01" verified />
      <DataTable<AdminSupportTicket>
        key={reloadKey}
        screenId="A7.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.id}
        emptyState={{ icon: <LifeBuoy size={28} strokeWidth={1.5} />, title: t.support.emptyTitle }}
        detailPanel={(r) => (
          <TicketThread ticket={r} onChanged={() => setReloadKey((k) => k + 1)} />
        )}
      />
    </>
  )
}
